import { MapPinIcon, MusicNotesIcon, PlusIcon, UsersIcon, XIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import {
  INSTAGRAM_AUDIO_PREVIEW_NOTE,
  formatAudioDuration,
} from '../../../shared/instagram-lookup';
import {
  INSTAGRAM_AUDIO_VOLUME,
  INSTAGRAM_COLLABORATORS_MAX,
  cleanInstagramLocationId,
  normalizeInstagramUsername,
} from '../../../shared/instagram-publish-options';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type {
  PerPlatformEntry,
  PublishOptionKey,
} from '@/components/ui/inspector/bodies/post/mediaKeysWriter';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SegmentedToggle } from '@/components/ui/segmented-toggle';
import { Slider } from '@/components/ui/slider';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useInstagramAudioSearch, useInstagramPlaceSearch } from '@/hooks/useInstagramLookup';
import { FIELD_LABEL } from '@/lib/ui-tokens';

type OptionsPatch = Partial<Record<PublishOptionKey, PerPlatformEntry[PublishOptionKey] | null>>;

export interface MetaPublishOptionsProps {
  platform: 'instagram' | 'facebook';
  entry: PerPlatformEntry | undefined;
  /** The resolved placement for the open channel. */
  placement: string | undefined;
  mediaKind: 'image' | 'video' | 'carousel' | 'text';
  /** Facebook: the post is a text post that carries a link. */
  hasLink?: boolean;
  disabled?: boolean;
  onChange: (patch: OptionsPatch) => void;
}

function Collaborators({
  value,
  disabled,
  onChange,
}: {
  value: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const handle = normalizeInstagramUsername(draft);
  const full = value.length >= INSTAGRAM_COLLABORATORS_MAX;
  const add = () => {
    if (!handle || full || value.includes(handle)) return;
    onChange([...value, handle]);
    setDraft('');
  };
  return (
    <div className="flex flex-col gap-2">
      <span className={FIELD_LABEL}>Collaborators</span>
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {value.map((name) => (
            <li
              key={name}
              className="flex items-center gap-1.5 rounded-full border border-border bg-card py-1 pl-3 pr-1 text-caption text-foreground"
            >
              <UsersIcon className="size-3.5 text-muted-foreground" aria-hidden />@{name}
              <Button
                type="button"
                variant="flat"
                size="icon-sm"
                aria-label={`Remove ${name}`}
                disabled={disabled}
                onClick={() => onChange(value.filter((n) => n !== name))}
              >
                <XIcon className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {full ? (
        <p className="text-caption text-muted-foreground">
          Instagram allows up to {INSTAGRAM_COLLABORATORS_MAX} collaborators.
        </p>
      ) : (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            add();
          }}
        >
          <Input
            value={draft}
            disabled={disabled}
            placeholder="@username"
            aria-label="Collaborator username"
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button type="submit" variant="flat" size="sm" disabled={disabled || !handle}>
            <PlusIcon className="size-3.5" />
            Invite
          </Button>
        </form>
      )}
      <p className="text-caption text-muted-foreground">
        Collaborators get an invite and the post appears on their profile once they accept.
      </p>
    </div>
  );
}

function Location({
  value,
  disabled,
  onChange,
}: {
  value: { id: string; name?: string | null } | undefined;
  disabled?: boolean;
  onChange: (next: { id: string; name?: string | null } | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 350);
  const search = useInstagramPlaceSearch(debounced, open);
  const typedId = cleanInstagramLocationId(query);
  const results = search.data?.state === 'ok' ? search.data.results : [];
  const note =
    search.data?.state === 'unavailable'
      ? search.data.message
      : search.data?.state === 'not_connected'
        ? 'Connect Instagram in Settings to search for places.'
        : null;

  return (
    <div className="flex flex-col gap-2">
      <span className={FIELD_LABEL}>Place</span>
      {value ? (
        <div className="flex items-center gap-1.5 self-start rounded-full border border-border bg-card py-1 pl-3 pr-1 text-caption text-foreground">
          <MapPinIcon className="size-3.5 text-muted-foreground" aria-hidden />
          {value.name || `Place ${value.id}`}
          <Button
            type="button"
            variant="flat"
            size="icon-sm"
            aria-label="Remove place"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            <XIcon className="size-3.5" />
          </Button>
        </div>
      ) : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="flat" size="sm" className="self-start" disabled={disabled}>
            <MapPinIcon className="size-3.5" />
            {value ? 'Change place' : 'Add a place'}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="flex w-80 flex-col gap-2">
          <label htmlFor="ig-place-search" className={FIELD_LABEL}>
            Search for a place
          </label>
          <Input
            id="ig-place-search"
            value={query}
            placeholder="Business or place name"
            onChange={(event) => setQuery(event.target.value)}
          />
          {search.isFetching ? (
            <p className="text-caption text-muted-foreground">Searching…</p>
          ) : null}
          {results.map((place) => (
            <button
              key={place.id}
              type="button"
              className="flex flex-col rounded-md p-1.5 text-left hover:bg-accent"
              onClick={() => {
                onChange({ id: place.id, name: place.name });
                setOpen(false);
              }}
            >
              <span className="text-sm text-foreground">{place.name}</span>
              {place.detail ? (
                <span className="text-caption text-muted-foreground">{place.detail}</span>
              ) : null}
            </button>
          ))}
          {typedId ? (
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onChange({ id: typedId, name: null });
                setOpen(false);
              }}
            >
              Use Facebook Page id {typedId}
            </Button>
          ) : null}
          {note ? <p className="text-caption text-muted-foreground">{note}</p> : null}
          {search.isError ? (
            <p className="text-caption text-muted-foreground">
              The search did not finish. You can still paste a Facebook Page id for the place.
            </p>
          ) : null}
          {debounced.trim().length >= 2 && search.isSuccess && !note && results.length === 0 ? (
            <p className="text-caption text-muted-foreground">No places found.</p>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function Audio({
  value,
  disabled,
  onChange,
}: {
  value: PerPlatformEntry['audio'];
  disabled?: boolean;
  onChange: (next: NonNullable<PerPlatformEntry['audio']> | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'music' | 'original_sound'>('music');
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 350);
  const search = useInstagramAudioSearch(debounced, kind, open);
  const results = search.data?.state === 'ok' ? search.data.results : [];
  const unavailable = search.data?.state === 'unavailable' ? search.data.message : null;
  const notConnected = search.data?.state === 'not_connected';
  const failed = search.data?.state === 'error' ? search.data.message : null;

  return (
    <div className="flex flex-col gap-2">
      <span className={FIELD_LABEL}>Audio</span>
      {value ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3">
          <div className="flex items-center gap-2">
            <MusicNotesIcon className="size-4 text-muted-foreground" aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-sm font-medium text-foreground">{value.name}</span>
              {value.artist ? (
                <span className="truncate text-caption text-muted-foreground">{value.artist}</span>
              ) : null}
            </span>
            <Button
              type="button"
              variant="flat"
              size="icon-sm"
              aria-label="Remove audio"
              disabled={disabled}
              onClick={() => onChange(null)}
            >
              <XIcon className="size-3.5" />
            </Button>
          </div>
          {(
            [
              ['audioVolume', 'Music volume'],
              ['videoVolume', 'Video sound volume'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex flex-col gap-1.5">
              <span className="flex justify-between text-caption text-muted-foreground">
                {label}
                <span>{value[key]}</span>
              </span>
              <Slider
                min={INSTAGRAM_AUDIO_VOLUME.min}
                max={INSTAGRAM_AUDIO_VOLUME.max}
                step={1}
                value={[value[key]]}
                disabled={disabled}
                thumbAriaLabel={label}
                onValueChange={([next]) => onChange({ ...value, [key]: next })}
              />
            </label>
          ))}
        </div>
      ) : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="flat" size="sm" className="self-start" disabled={disabled}>
            <MusicNotesIcon className="size-3.5" />
            {value ? 'Change audio' : 'Choose audio'}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="flex w-96 max-w-[90vw] flex-col gap-2">
          <SegmentedToggle
            value={kind}
            onValueChange={(next) => setKind(next as 'music' | 'original_sound')}
            variant="pills"
            size="sm"
            options={[
              { value: 'music', label: 'Music' },
              { value: 'original_sound', label: 'Original sounds' },
            ]}
          />
          <Input
            value={query}
            placeholder={kind === 'music' ? 'Search songs or artists' : 'Search original sounds'}
            aria-label="Search audio"
            onChange={(event) => setQuery(event.target.value)}
          />
          {search.isFetching ? (
            <p className="text-caption text-muted-foreground">Searching…</p>
          ) : null}
          <ul className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
            {results.map((track) => (
              <li key={track.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md p-1.5 text-left hover:bg-accent"
                  onClick={() => {
                    onChange({
                      id: track.id,
                      name: track.title,
                      artist: track.artist,
                      kind: track.kind,
                      audioVolume: value?.audioVolume ?? INSTAGRAM_AUDIO_VOLUME.default,
                      videoVolume: value?.videoVolume ?? INSTAGRAM_AUDIO_VOLUME.default,
                    });
                    setOpen(false);
                  }}
                >
                  {track.artworkUrl ? (
                    <img src={track.artworkUrl} alt="" className="size-9 rounded object-cover" />
                  ) : (
                    <span className="flex size-9 items-center justify-center rounded bg-muted">
                      <MusicNotesIcon className="size-4 text-muted-foreground" aria-hidden />
                    </span>
                  )}
                  <span className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="truncate text-sm text-foreground">{track.title}</span>
                    <span className="truncate text-caption text-muted-foreground">
                      {[track.artist, formatAudioDuration(track.durationMs)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {unavailable ? <p className="text-caption text-muted-foreground">{unavailable}</p> : null}
          {notConnected ? (
            <p className="text-caption text-muted-foreground">
              Connect Instagram in Settings to search for audio.
            </p>
          ) : null}
          {failed ? <p className="text-caption text-muted-foreground">{failed}</p> : null}
          {search.isError ? (
            <p className="text-caption text-muted-foreground">
              The search did not finish. Try again in a moment.
            </p>
          ) : null}
        </PopoverContent>
      </Popover>
      <p className="text-caption text-muted-foreground">
        {value
          ? INSTAGRAM_AUDIO_PREVIEW_NOTE
          : 'Pick a song or original sound from Instagram’s library. Only audio Instagram licenses for apps like Mora appears.'}
      </p>
    </div>
  );
}

function LinkPreview({
  value,
  disabled,
  onChange,
}: {
  value: PerPlatformEntry['link'];
  disabled?: boolean;
  onChange: (next: NonNullable<PerPlatformEntry['link']> | null) => void;
}) {
  const link = value ?? {};
  const set = (key: 'name' | 'description' | 'picture', text: string) => {
    const next = { ...link, [key]: text };
    if (!text.trim()) delete next[key];
    onChange(Object.keys(next).length ? next : null);
  };
  return (
    <div className="flex flex-col gap-2">
      <span className={FIELD_LABEL}>Link preview</span>
      <Input
        value={link.name ?? ''}
        disabled={disabled}
        placeholder="Title"
        aria-label="Link preview title"
        onChange={(event) => set('name', event.target.value)}
      />
      <Input
        value={link.description ?? ''}
        disabled={disabled}
        placeholder="Description"
        aria-label="Link preview description"
        onChange={(event) => set('description', event.target.value)}
      />
      <Input
        value={link.picture ?? ''}
        disabled={disabled}
        placeholder="Image link (https)"
        aria-label="Link preview image"
        onChange={(event) => set('picture', event.target.value)}
      />
      <p className="text-caption text-muted-foreground">
        Facebook only applies these to links your Page owns. For any other link it shows its own
        preview.
      </p>
    </div>
  );
}

export function MetaPublishOptions({
  platform,
  entry,
  placement,
  mediaKind,
  hasLink,
  disabled,
  onChange,
}: MetaPublishOptionsProps) {
  if (platform === 'facebook') {
    return hasLink && mediaKind === 'text' ? (
      <LinkPreview
        value={entry?.link}
        disabled={disabled}
        onChange={(next) => onChange({ link: next })}
      />
    ) : null;
  }
  if (mediaKind === 'text') return null;
  const isStory = placement === 'story';
  const isReel = placement === 'reel' && mediaKind === 'video';
  if (isStory) return null;
  return (
    <div className="flex flex-col gap-4">
      <Collaborators
        value={entry?.collaborators ?? []}
        disabled={disabled}
        onChange={(next) => onChange({ collaborators: next })}
      />
      <Location
        value={entry?.location}
        disabled={disabled}
        onChange={(next) => onChange({ location: next })}
      />
      {isReel ? (
        <Audio
          value={entry?.audio}
          disabled={disabled}
          onChange={(next) => onChange({ audio: next })}
        />
      ) : null}
    </div>
  );
}
